import { useQueryClient } from "@tanstack/react-query";
import { lazy, Suspense, useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { NavLink, Route, Routes, useLocation } from "react-router";
import { api, getToken, setToken, useActors, useBridge, useInbox, useMe, useThreads, useWhere } from "./api.ts";
import { Pill } from "./calm.tsx";
import { PresenceAvatars, type PresencePerson } from "./components/ui/presence-avatars.tsx";
import { PersonCard } from "./components/PersonCard.tsx";
import { Backdrop } from "./components/Backdrop.tsx";
import { Logo } from "./components/Logo.tsx";
import { CommandPalette } from "./components/CommandPalette.tsx";
import { LiveProvider, useLive } from "./live.tsx";
import { ProjectProvider, ProjectSwitcher } from "./project.tsx";
import { presenceHumans } from "./roster.ts";
import { whereLabel } from "./where.ts";
import { ActivityPage } from "./pages/Activity.tsx";
import { ChiefPage } from "./pages/Chief.tsx";
import { BridgePage } from "./pages/Bridge.tsx";
import { ConnectPage } from "./pages/Connect.tsx";
import { ConversationsPage } from "./pages/Conversations.tsx";
import { InboxPage } from "./pages/Inbox.tsx";
import { IntentPage } from "./pages/Intent.tsx";
import { IdeasPage } from "./pages/Ideas.tsx";
import { IdeaStatusPage } from "./pages/IdeaStatusPage.tsx";
import { AddNew } from "./components/AddNew.tsx";
import { NavMore } from "./components/NavMore.tsx";
import { movedPrimaries, PRIMARY_NAV, type NavWidth } from "./lib/nav.ts";
import { useMedia } from "./lib/use-media.ts";
import { LessonsPage } from "./pages/Lessons.tsx";
import { ProjectPage } from "./pages/Project.tsx";
import { ProjectsPage } from "./pages/Projects.tsx";
import { RoutinePage } from "./pages/Routine.tsx";
import { RoutineEditRoute, RoutineFormPage } from "./pages/RoutineForm.tsx";
import { RoutinesPage } from "./pages/Routines.tsx";
import { RulesPage } from "./pages/Rules.tsx";
import { RunPage } from "./pages/Run.tsx";
import { SkillsPage } from "./pages/Skills.tsx";
import { StatusPage } from "./pages/Status.tsx";
import { TaskPage } from "./pages/Task.tsx";
import { TeamPage } from "./pages/Team.tsx";
import { ProfilePage } from "./pages/Profile.tsx";
import { AgentConfigPage } from "./pages/AgentConfig.tsx";
import { AgentImportPage } from "./pages/AgentImport.tsx";
import { showRoomie } from "./buddy/mount.ts";
import { Button, cx, ErrorNote, inputCls } from "./ui.tsx";

// three.js is heavy: the Room loads only when someone opens it.
const OfficePage = lazy(() => import("./pages/Office.tsx").then((m) => ({ default: m.OfficePage })));
// Roomie loads after the page, so the companion never slows the first paint.
const Roomie = lazy(() => import("./buddy/Buddy.tsx"));

export function App() {
  const [token, setTok] = useState(getToken());
  const me = useMe();
  if (!token || me.error) {
    return (
      <SignIn
        onSignedIn={(t) => {
          setToken(t);
          setTok(t);
          void me.refetch();
        }}
        error={token ? me.error : null}
      />
    );
  }
  if (!me.data) return <div className="p-10 font-mono text-ink-3">loading…</div>;
  return (
    <LiveProvider>
      <ProjectProvider>
        <Shell
          onSignOut={() => {
            setToken(null);
            setTok(null);
          }}
        />
        <CommandPalette />
      </ProjectProvider>
    </LiveProvider>
  );
}

function SignIn({ onSignedIn, error }: { onSignedIn: (token: string) => void; error: unknown }) {
  const [value, setValue] = useState("");
  const [err, setErr] = useState<unknown>(error);
  const qc = useQueryClient();
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api("GET", "/me", undefined, value.trim());
      qc.clear();
      onSignedIn(value.trim());
    } catch (x) {
      setErr(x);
    }
  };
  return (
    <main className="mx-auto grid min-h-screen max-w-5xl items-center gap-12 px-6 md:grid-cols-[1.2fr_1fr]">
      <div>
        <h1 className="font-display text-5xl font-extrabold leading-[0.95] tracking-tight md:text-6xl">
          Agents build.
          <br />
          Humans decide.
        </h1>
        <p className="mt-5 max-w-md text-[15px] text-ink-2">
          One room for your team and its agents: who is on what, why every decision was made, and what needs you now.
        </p>
      </div>
      <form onSubmit={submit} className="ticket p-6 pl-9">
        <label htmlFor="token" className="text-[13px] font-medium text-ink-2">
          Your token
        </label>
        <input
          id="token"
          type="password"
          autoComplete="off"
          className={cx(inputCls, "mt-2 font-mono")}
          placeholder="rm_…"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <p className="mt-2 text-[12px] text-ink-3">
          First time? Run <code className="font-mono">roomate init --name "You"</code> on the server, or ask an admin for a token.
        </p>
        <div className="mt-4 space-y-3">
          <ErrorNote error={err} />
          <Button variant="primary" type="submit" disabled={!value.trim()} className="w-full">
            Enter
          </Button>
        </div>
      </form>
    </main>
  );
}

/** True below Tailwind's `sm` breakpoint (640px), tracked live so a resize swaps the chip label. */
/** How much of the nav fits: every primary from xl, the short ones from lg, only "More" below lg. */
function useNavWidth(): NavWidth {
  const belowLg = useMedia("(max-width: 1023.98px)");
  const belowXl = useMedia("(max-width: 1279.98px)");
  return belowLg ? "narrow" : belowXl ? "medium" : "wide";
}

function Shell({ onSignOut }: { onSignOut: () => void }) {
  const me = useMe().data!;
  const inbox = useInbox();
  const threads = useThreads({ actorId: "me" });
  const { connected, presence } = useLive();
  const actors = useActors().data ?? [];
  const lanes = useBridge().data?.agentLanes ?? [];
  const { pathname } = useLocation();
  // The office page is its own full-bleed 3D scene, so the rail and Roomie stay off it.
  const showPresenceRail = showRoomie(pathname);
  // Who is in the room right now: people with a live session, and every agent with a run.
  // `online` mirrors the header's own dot (`connected`) for the signed-in user. For others,
  // presence records never expire client-side (live.tsx only ever adds to the map), so without
  // this check a stale entry would read as a confident "live now" green dot forever; this only
  // re-evaluates on render, so a dot can lag a little past the 5-minute mark between renders.
  const here = [
    ...presenceHumans(actors, me.id, new Set(Object.keys(presence))).map((a) => ({
        id: a.id,
        name: a.name.replace(/-agent$/, ""),
        online: a.id === me.id ? connected : Date.now() - presence[a.id]!.at < 5 * 60_000,
      })),
    ...actors
      .filter((a) => a.kind === "agent" && lanes.some((l) => l.actorId === a.id))
      .map((a) => ({ id: a.id, name: a.name.replace(/-agent$/, ""), online: true })),
  ];
  // The rail's person card: one open at a time, anchored to the avatar that opened it.
  const [card, setCard] = useState<{ id: string; trigger: HTMLButtonElement } | null>(null);
  const cardRef = useRef(card);
  cardRef.current = card;
  const closeCard = useCallback((restoreFocus: boolean, leaving = false) => {
    const current = cardRef.current;
    if (restoreFocus && current) {
      // A tile that is leaving is still mid-exit and about to unmount, so hand focus to a tile that stays.
      const stays = leaving
        ? [...document.querySelectorAll<HTMLElement>('[data-testid="presence-rail"] [data-testid="presence-avatar"]')].find((el) => el !== current.trigger)
        : current.trigger;
      if (stays?.isConnected) stays.focus();
    }
    setCard(null);
  }, []);
  // The card belongs to the rail: a route change, the rail unmounting, or its person leaving closes it.
  // If the card held focus when it goes, hand focus back to its avatar instead of dropping it on <body>.
  const closeWithFocusHandoff = useCallback((leaving = false) => {
    const focused = document.activeElement;
    closeCard(Boolean(focused && focused.closest('[data-testid="person-card"]')), leaving);
  }, [closeCard]);
  const cardId = card?.id;
  const cardHere = cardId ? here.some((p) => p.id === cardId) : true;
  useEffect(() => closeWithFocusHandoff(), [pathname, closeWithFocusHandoff]);
  useEffect(() => {
    if (!cardHere) closeWithFocusHandoff(true);
  }, [cardHere, closeWithFocusHandoff]);
  const selectPerson = (person: PresencePerson, trigger: HTMLButtonElement) => {
    if (card?.id === person.id) return closeCard(true);
    setCard({ id: person.id, trigger });
  };
  // Nothing renders while loading or on error: `where` stays undefined and the chip is simply absent.
  const whereData = useWhere().data;
  const where = whereData ? whereLabel(whereData) : null;
  const navWidth = useNavWidth();
  const moved = movedPrimaries(navWidth);
  const count = inbox.data?.length ?? 0;
  const conversationUnread = (threads.data ?? []).reduce((sum, thread) => sum + thread.unreadCount, 0);
  // Pill nav: each link is its own pill inside one glass capsule; the active page is the filled one.
  const link = ({ isActive }: { isActive: boolean }) =>
    cx(
      "shrink-0 rounded-full px-3.5 py-1.5 text-[13px] transition-colors",
      isActive ? "bg-paper-2 text-ink shadow-sm ring-1 ring-rule/60" : "text-ink-3 hover:bg-paper-2/60 hover:text-ink",
    );

  const toggleTheme = () => {
    const root = document.documentElement;
    const dark = root.dataset.theme ? root.dataset.theme === "dark" : matchMedia("(prefers-color-scheme: dark)").matches;
    root.dataset.theme = dark ? "light" : "dark";
    try {
      localStorage.setItem("roomate.theme", root.dataset.theme);
    } catch {
      // storage blocked
    }
  };

  return (
    <div className="min-h-screen">
      <Backdrop />
      {/* The top bar: two floating glass pills side by side and centred over the page, navigation then actions, held 12px off the top edge. */}
      <header className="sticky top-0 z-30 px-4 pt-3 md:px-6">
        <div className="mx-auto flex h-14 max-w-[1400px] items-center justify-center gap-3">
          <div className="glass flex h-full min-w-0 items-center gap-2 rounded-full border border-rule/50 pl-4 pr-1.5 shadow-sm sm:gap-3 sm:pl-5">
            <NavLink to="/" className="relative flex shrink-0 items-center" aria-label="Roomate home">
              {/* Below sm only the symbol shows, so both pills fit a phone. */}
              <Logo className="max-sm:[&>span:last-child]:hidden" />
              {count > 0 && <span className="os-breathe-signal absolute -right-2.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-signal" aria-hidden />}
            </NavLink>
            <ProjectSwitcher />
            <nav className="flex shrink-0 items-center gap-0.5" aria-label="Main">
              {PRIMARY_NAV.filter((item) => !moved.includes(item)).map((item) => (
                <NavLink key={item.to} to={item.to} end={item.end} className={link}>
                  {item.label}
                  {item.to === "/inbox" && count > 0 && (
                    <span className="ml-1 tabular-nums text-signal" data-testid="inbox-count">
                      {count}
                    </span>
                  )}
                  {item.to === "/conversations" && conversationUnread > 0 && (
                    <span className="ml-1 tabular-nums text-signal" data-testid="conversations-count">
                      {conversationUnread}
                    </span>
                  )}
                </NavLink>
              ))}
              <NavMore moved={moved} linkClass={link} />
            </nav>
          </div>
          <div className="glass flex h-full min-w-0 shrink items-center gap-2 rounded-full border border-rule/50 pl-2 pr-4 text-[13px] shadow-sm sm:gap-3 sm:pr-5">
            <AddNew />
            {where && (
              <span data-testid="where-chip" title={where.title} className="hidden min-w-0 max-w-[11rem] shrink sm:inline">
                <Pill tone={where.tone} className="max-w-full whitespace-nowrap">
                  {/* Short "Hub" / "Node" below xl so both pills stay on one line. One text node, so the chip reads as one label. */}
                  {navWidth !== "wide" ? where.short : <span className="block min-w-0 truncate">{where.text}</span>}
                </Pill>
              </span>
            )}
            <span className="flex shrink-0 items-center gap-1.5" title={connected ? "Live" : "Reconnecting"}>
              <span className={cx("h-1.5 w-1.5 rounded-full", connected ? "bg-ok" : "bg-ink-3")} aria-label={connected ? "live" : "offline"} />
              <span className="hidden text-ink-2 xl:inline">{me.name}</span>
            </span>
            <button onClick={toggleTheme} aria-label="Toggle theme" className="hidden shrink-0 text-ink-3 hover:text-ink sm:inline">
              ◐
            </button>
            <button onClick={onSignOut} className="shrink-0 whitespace-nowrap text-ink-3 hover:text-ink">
              Sign out
            </button>
          </div>
        </div>
      </header>
      {showPresenceRail && (
        <div data-testid="presence-rail" className="fixed left-4 top-24 z-20 hidden lg:block">
          {/* A vertical pill holds the chips, so the rail reads as one object rather than loose dots. */}
          <div className="glass rounded-full border border-rule/50 px-2 py-3">
            <PresenceAvatars
              people={here}
              max={5}
              size={32}
              gap={8}
              label="In the room"
              orientation="vertical"
              onSelect={selectPerson}
              selectedId={card?.id ?? null}
            />
          </div>
          {card && (
            <PersonCard
              key={card.id}
              actorId={card.id}
              online={here.find((p) => p.id === card.id)?.online}
              trigger={card.trigger}
              layoutKey={here.map((p) => p.id).join(",")}
              onClose={closeCard}
            />
          )}
        </div>
      )}
      {showPresenceRail && (
        <Suspense fallback={null}>
          <Roomie />
        </Suspense>
      )}
      <main className={cx("relative z-10 mx-auto max-w-[1400px] px-4 py-6 md:px-6", showPresenceRail && "lg:px-24")}>
        <Routes>
          <Route path="/" element={<BridgePage />} />
          <Route path="/inbox" element={<InboxPage />} />
          <Route path="/activity" element={<ActivityPage />} />
          <Route path="/chief" element={<ChiefPage />} />
          <Route path="/routines" element={<RoutinesPage />} />
          <Route path="/routines/new" element={<RoutineFormPage />} />
          <Route path="/routines/:id" element={<RoutinePage />} />
          <Route path="/routines/:id/edit" element={<RoutineEditRoute />} />
          <Route path="/conversations" element={<ConversationsPage />} />
          <Route path="/conversations/:threadId" element={<ConversationsPage />} />
          <Route path="/office" element={<Suspense fallback={<div className="h-[calc(100dvh-68px)] bg-[#07070a]" />}><OfficePage /></Suspense>} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/ideas" element={<IdeasPage />} />
          <Route path="/ideas/:id" element={<IdeaStatusPage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/team/:actorId" element={<ProfilePage />} />
          <Route path="/agents/import/:previewId" element={<AgentImportPage />} />
          <Route path="/agents/:id/config" element={<AgentConfigPage />} />
          <Route path="/skills" element={<SkillsPage />} />
          <Route path="/rules" element={<RulesPage />} />
          <Route path="/connect" element={<ConnectPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/:id" element={<ProjectPage />} />
          <Route path="/projects/:id/lessons" element={<LessonsPage />} />
          <Route path="/tasks/:id" element={<TaskPage />} />
          <Route path="/runs/:id" element={<RunPage />} />
          <Route path="/intents/:id" element={<IntentPage />} />
          <Route path="*" element={<p className="text-ink-3">Not found.</p>} />
        </Routes>
      </main>
    </div>
  );
}
